/** One review's scores, as the dish review list returns them. */
export interface ReviewScores {
  rating: number
  foodQualityRating?: number
  packagingRating?: number
}

export interface RatingSummary {
  /** Reviews counted here (the list is capped, so this can trail the total). */
  counted: number
  /** Share of counted reviews at each star value, 5 down to 1. */
  distribution: readonly { stars: number; count: number; share: number }[]
  /** Average of the optional dimensions, over reviews that rated them. */
  foodQuality?: { average: number; count: number }
  packaging?: { average: number; count: number }
}

const average = (values: readonly number[]) =>
  values.length === 0 ? undefined : { average: values.reduce((sum, v) => sum + v, 0) / values.length, count: values.length }

/**
 * Break a dish's reviews down by star and by dimension.
 *
 * Reads the same verified-purchase list the headline average is computed from,
 * so the bars always agree with it. A food-quality or packaging score of 0
 * means "not rated" and is left out rather than counted as a bad score.
 */
export const summarizeReviews = (reviews: readonly ReviewScores[]): RatingSummary => {
  const valid = reviews.filter((r) => Number.isInteger(r.rating) && r.rating >= 1 && r.rating <= 5)
  const counted = valid.length
  const distribution = [5, 4, 3, 2, 1].map((stars) => {
    const count = valid.filter((r) => r.rating === stars).length
    return { stars, count, share: counted ? count / counted : 0 }
  })
  const dimension = (pick: (r: ReviewScores) => number | undefined) =>
    average(valid.map(pick).filter((v): v is number => typeof v === 'number' && v >= 1 && v <= 5))
  return {
    counted,
    distribution,
    foodQuality: dimension((r) => r.foodQualityRating),
    packaging: dimension((r) => r.packagingRating),
  }
}
